import { countrySpecs, isValidIBAN } from 'ibantools'
import * as R from 'ramda'
import {
  CountryCode,
  countryData,
  CountryDataLanguage,
  getCountryNameByCountryCode,
} from '@kenno/resources'

import type { BeneficiaryType } from '../TerminateForm/initialValues'
import { validationErrors } from '../../../../common/validations'

const FINNISH_COUNTRY_CODE: CountryCode = 'FI'

export const DEFAULT_BANK_COUNTRY_CODE: CountryCode = FINNISH_COUNTRY_CODE

export const countryCodeFromIban = (
  iban: string | null
): CountryCode | null => {
  const electronicIban = iban?.replace(/\s/g, '') ?? ''

  if (!isValidIBAN(electronicIban)) {
    return null
  }

  const ibanCountryCode = electronicIban.slice(0, 2)

  return (
    R.keys(countryData).find(
      (countryCode) => countryCode === ibanCountryCode
    ) ?? null
  )
}

export const shouldShowBicField = (
  bankCountryCode: CountryCode | null,
  iban: string | null
): boolean => {
  const electronicIban = iban?.replace(/\s/g, '') ?? ''

  return (
    bankCountryCode !== FINNISH_COUNTRY_CODE ||
    (isValidIBAN(electronicIban) &&
      !electronicIban.startsWith(FINNISH_COUNTRY_CODE))
  )
}

export const isIbanCountry = (countryCode: CountryCode): boolean =>
  Boolean(countrySpecs[countryCode]?.chars)

const countryDataLanguages: CountryDataLanguage[] = ['fi', 'sv', 'en']

export const ibanCountryName = (
  iban: string | null,
  language: string
): string | null =>
  getCountryNameByCountryCode(
    countryCodeFromIban(iban),
    countryDataLanguages.find(
      (countryDataLanguage) => countryDataLanguage === language
    ) ?? 'fi'
  )

export type BankCountryError =
  | typeof validationErrors.required
  | typeof validationErrors.bank_country_mismatch

export const resolveBankCountryError = ({
  beneficiaryName,
  iban,
  bankCountryCode,
  removeExistingBeneficiary,
}: BeneficiaryType): BankCountryError | null => {
  const hasName = !!beneficiaryName?.trim()
  const hasIban = !!iban?.trim()

  if (removeExistingBeneficiary || (!hasName && !hasIban)) {
    return null
  }

  if (bankCountryCode === null) {
    return validationErrors.required
  }

  const ibanCountryCode = countryCodeFromIban(iban)

  return ibanCountryCode !== null && ibanCountryCode !== bankCountryCode
    ? validationErrors.bank_country_mismatch
    : null
}
